import { COMPANY_PDF_LOGO } from "@/lib/brand";

// Each waybill row is already one (length, quantity) pair in the new app
// (the import-from-quotation flow fans the breakdown into individual rows),
// so the print view renders one table row per item without re-flattening.
export interface WaybillPrintItem {
  description: string;
  unit: string;
  length: number;
  quantity: number;
}

export interface WaybillPrintData {
  number: string;
  customerName: string;
  customerPhone?: string;
  deliveryAddress?: string;
  quotationRef?: string;
  waybillDate?: string;
  driverName?: string;
  vehicleNumber?: string;
  status?: string;
  notes?: string;
  // Header-strip fields. Invoice no/date come from a linked sales invoice
  // and Profile / Color from the source quotation. They are optional —
  // when absent the corresponding strip cell renders blank rather than
  // crashing (older waybills, or ones whose source data wasn't passed
  // through, simply leave these empty).
  invoiceNumber?: string;
  invoiceDate?: string;
  profile?: string;
  color?: string;
  items: WaybillPrintItem[];
}

export interface WaybillPrintCompany {
  name: string;
  address: string;
  phone: string;
  email: string;
}

// Two-decimal, thousands-separated number used across the items table to
// match the sample (3.50 / 16.00 / 56.00).
function formatWaybillQty(n: number) {
  return Number(n || 0).toLocaleString("en-GH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// dd-mm-yyyy formatting to match the sample (03-06-2026). Date-only strings
// (YYYY-MM-DD) are reordered directly so timezone parsing never shifts the
// day; full ISO timestamps fall back to Date parsing.
function formatDmy(input?: string | null) {
  if (!input) return "";
  const s = String(input);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
}

function effectiveLength(length: number) {
  return length > 0 ? length : 1;
}

export default function WaybillPrint({
  w,
  company,
  preparedBy = "System",
}: {
  w: WaybillPrintData;
  company: WaybillPrintCompany;
  preparedBy?: string;
}) {
  let qtySum = 0;
  let totalQtySum = 0;
  const rows = w.items.map((it) => {
    const len = effectiveLength(Number(it.length || 0));
    const qty = Number(it.quantity || 0);
    const total = len * qty;
    qtySum += qty;
    totalQtySum += total;
    return { description: it.description, unit: it.unit, len, qty, total };
  });

  const strip: Array<{ label: string; value: string }> = [
    { label: "WAYBILL NO", value: w.number || "" },
    { label: "WAYBILL DATE", value: formatDmy(w.waybillDate) },
    { label: "INVOICE NO", value: w.invoiceNumber || "" },
    { label: "INVOICE DATE", value: formatDmy(w.invoiceDate) },
    { label: "PROFILE", value: w.profile || "" },
    { label: "COLOR", value: w.color || "" },
  ];

  return (
    <div
      className="print-area relative bg-white text-slate-900 p-5 font-sans overflow-hidden"
      style={{ width: "800px" }}
    >
      <div className="flex justify-between items-start border-b-2 border-slate-300 pb-2">
        <div className="flex items-center gap-4">
          <img
            src={COMPANY_PDF_LOGO}
            alt="Company logo"
            className="h-20 w-auto object-contain"
          />
          <div>
            <div className="text-lg font-bold uppercase leading-tight">
              {company.name}
            </div>
            <div className="text-[11px] mt-0.5">{company.address}</div>
            <div className="text-[11px]">Tel: {company.phone}</div>
            <div className="text-[11px]">Email: {company.email}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xl font-bold tracking-wide leading-tight">
            WAYBILL
          </div>
          <div className="text-sm font-mono mt-0.5">{w.number || ""}</div>
        </div>
      </div>

      {/* Delivery meta block — moved to the TOP, immediately under the
          company header / document title. */}
      <div className="mt-3 grid grid-cols-2 gap-3 text-[13px] leading-tight">
        <div>
          <span className="text-[10px] uppercase text-slate-500">Deliver To: </span>
          <span className="font-semibold">{w.customerName}</span>
        </div>
        <div>
          <span className="text-[10px] uppercase text-slate-500">Phone: </span>
          <span className="font-semibold">{w.customerPhone || "—"}</span>
        </div>
        <div className="col-span-2">
          <span className="text-[10px] uppercase text-slate-500">
            Delivery Address:{" "}
          </span>
          <span className="font-semibold">{w.deliveryAddress || "—"}</span>
        </div>
        <div>
          <span className="text-[10px] uppercase text-slate-500">
            Quotation Ref:{" "}
          </span>
          <span className="font-semibold">{w.quotationRef || "—"}</span>
        </div>
        <div>
          <span className="text-[10px] uppercase text-slate-500">Driver: </span>
          <span className="font-semibold">{w.driverName || "—"}</span>
        </div>
        <div>
          <span className="text-[10px] uppercase text-slate-500">Vehicle: </span>
          <span className="font-semibold">{w.vehicleNumber || "—"}</span>
        </div>
      </div>

      {/* Horizontal header strip sits directly above the items table:
          WAYBILL NO | WAYBILL DATE | INVOICE NO | INVOICE DATE | PROFILE | COLOR */}
      <table className="w-full mt-3 text-[11px] border-collapse">
        <thead>
          <tr className="bg-slate-100">
            {strip.map((cell) => (
              <th
                key={cell.label}
                className="border border-slate-300 px-2 py-1.5 text-left uppercase tracking-wide text-[10px] text-slate-600"
              >
                {cell.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {strip.map((cell) => (
              <td
                key={cell.label}
                className="border border-slate-300 px-2 py-1.5 font-semibold align-middle"
              >
                {cell.value}
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      <table className="w-full mt-3 text-[11px] border-collapse">
        <thead>
          <tr className="bg-slate-100">
            <th className="border border-slate-300 px-2 py-1.5 text-center w-8">
              NO.
            </th>
            <th className="border border-slate-300 px-2 py-1.5 text-left">
              PRODUCT DESCRIPTION
            </th>
            <th className="border border-slate-300 px-2 py-1.5 text-left w-16">
              UNIT
            </th>
            <th className="border border-slate-300 px-2 py-1.5 text-right">
              LENGTH
            </th>
            <th className="border border-slate-300 px-2 py-1.5 text-right">
              QTY
            </th>
            <th className="border border-slate-300 px-2 py-1.5 text-right">
              TOTAL QUANTITY
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((it, i) => (
            <tr key={i}>
              <td className="border border-slate-300 px-2 py-1.5 text-center align-middle">
                {i + 1}
              </td>
              <td className="border border-slate-300 px-2 py-1.5 align-middle">
                {it.description}
              </td>
              <td className="border border-slate-300 px-2 py-1.5 align-middle">
                {it.unit}
              </td>
              <td className="border border-slate-300 px-2 py-1.5 text-right font-mono align-middle">
                {formatWaybillQty(it.len)}
              </td>
              <td className="border border-slate-300 px-2 py-1.5 text-right font-mono align-middle">
                {formatWaybillQty(it.qty)}
              </td>
              <td className="border border-slate-300 px-2 py-1.5 text-right font-mono font-semibold align-middle">
                {formatWaybillQty(it.total)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-slate-100">
            <td
              className="border border-slate-300 px-2 py-1.5 text-right font-semibold"
              colSpan={4}
            >
              TOTAL
            </td>
            <td className="border border-slate-300 px-2 py-1.5 text-right font-mono font-semibold">
              {formatWaybillQty(qtySum)}
            </td>
            <td className="border border-slate-300 px-2 py-1.5 text-right font-mono font-semibold">
              {formatWaybillQty(totalQtySum)}
            </td>
          </tr>
        </tfoot>
      </table>

      {w.notes ? (
        <p className="mt-3 text-[10px] text-slate-600">
          <strong>Notes:</strong> {w.notes}
        </p>
      ) : null}

      <div className="mt-3 pt-2 border-t border-slate-200 grid grid-cols-2 gap-8 text-[11px]">
        <div>
          <div className="text-slate-500 uppercase tracking-wide">
            Prepared By
          </div>
          <div className="font-semibold mt-0.5">{preparedBy}</div>
          <div className="mt-6 border-b border-slate-400" />
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-wide">
            Received By (Signature)
          </div>
          <div className="mt-6 border-b border-slate-400" />
        </div>
      </div>
    </div>
  );
}
